import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { useCallback } from 'react';
import FormSelectPaginate from '@/modules/admin-dashboard/components/FormSelectPaginate.tsx';
import RegisterSelectPaginate from '@/modules/admin-dashboard/components/RegisterSelectPaginate.tsx';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { Filters } from '@/modules/admin-dashboard/pages/Tasks.tsx';
import { FilterItem, SelectOption, SelectPaginateOption, Spinner } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { RegisterResult, TaskListMode } from 'compliance-api';

const complianceOptions: SelectOption<RegisterResult, string>[] = Object.values(RegisterResult).map(
  value => ({
    label: fixNameEnum(value),
    value,
  }),
);

const modeOptions: SelectOption<TaskListMode, string>[] = [
  { label: 'To do', value: TaskListMode.Todo },
  { label: 'Upcoming', value: TaskListMode.Upcoming },
  { label: 'Overdue', value: TaskListMode.Overdue },
  { label: 'History', value: TaskListMode.History },
];

interface TasksFiltersProps {
  mode: TaskListMode;
  filters: Filters;
  className?: string;
  onChange: (cb: (filters: Filters) => Filters) => void;
  showModeFilter?: boolean;
  isFetching?: boolean;
}

const TasksFilters = ({
  filters,
  mode,
  className,
  onChange,
  showModeFilter = false,
  isFetching = false,
}: TasksFiltersProps) => {
  const { register, form, assigneeUser, from_date, to_date } = filters;
  const handleChangeForm = useCallback(
    (form: SelectOption<number> | null) => {
      onChange(filters => (form ? { ...filters, form } : omit(filters, ['form'])));
    },
    [onChange],
  );

  const handleChangeRegister = useCallback(
    (register: SelectOption<number> | null) => {
      onChange(filters => (register ? { ...filters, register } : omit(filters, ['register'])));
    },
    [onChange],
  );

  const handleChangeAssigneeUser = useCallback(
    (assigneeUser: SelectPaginateOption<number> | null) => {
      onChange(filters =>
        assigneeUser ? { ...filters, assigneeUser } : omit(filters, ['assigneeUser']),
      );
    },
    [onChange],
  );

  const handleChangeDateFilter = useCallback(
    (dates: [Dayjs, Dayjs] | null) => {
      const [from_date, to_date] = dates || [];
      onChange(filters =>
        dates
          ? { ...filters, from_date, to_date }
          : omit(filters, ['from_date', 'to_date', 'show_with_recurrency_date']),
      );
    },
    [onChange],
  );

  const handleChangeComplianceFilter = useCallback(
    (filter_by_compliance_status: RegisterResult | null) => {
      onChange(filters =>
        filter_by_compliance_status
          ? { ...filters, filter_by_compliance_status }
          : omit(filters, ['filter_by_compliance_status']),
      );
    },
    [onChange],
  );

  const handleChangeModeFilter = useCallback(
    (mode: TaskListMode | null) => {
      onChange(filters => (mode ? { ...filters, mode } : omit(filters, ['mode'])));
    },
    [onChange],
  );

  return (
    <div
      className={classNames(
        'flex flex-wrap justify-start gap-3 items-center [&>*]:w-fit [&>*]:max-w-[336px]', //[&>*]:flex-[1_0_225px] [&>*]:max-w-[325px]
        className,
      )}
    >
      {showModeFilter && (
        <FilterItem
          type="select"
          label="Mode"
          placeholder="Select mode"
          classes={{
            label: 'w-[85px] shrink-0',
            element: 'w-full [&>div]:!min-w-0',
          }}
          options={modeOptions}
          value={filters.mode}
          onChange={handleChangeModeFilter}
          isClearable={false}
        />
      )}
      <FilterItem
        label="Form"
        type="custom"
        classes={{
          element: '[&_*]:!border-none w-full !py-0 !pr-0.5',
          label: 'w-[85px] shrink-0',
        }}
      >
        <FormSelectPaginate
          isClearable
          placeholder="Any"
          value={form || null}
          onChange={handleChangeForm}
          formFieldTitle=""
          isMulti={false}
          className='[&_[class*="control"]]:!p-0 [&_[class*="control"]]:min-w-[200px]'
        />
      </FilterItem>
      <FilterItem
        label="Label"
        type="custom"
        classes={{
          element: '[&_*]:!border-none w-full !py-0 !pr-0.5',
          label: 'w-[85px] shrink-0',
        }}
      >
        <RegisterSelectPaginate
          isClearable
          value={register || null}
          placeholder="Any"
          onChange={handleChangeRegister}
          isMulti={false}
          className='[&_[class*="control"]]:!p-0 [&_[class*="control"]]:min-w-[200px]'
        />
      </FilterItem>
      <FilterItem
        label="Assignee"
        type="custom"
        classes={{
          element: '[&_*]:!border-none w-full !py-0 !pr-1',
          label: 'w-[85px] shrink-0',
        }}
      >
        <UsersSelectPaginated
          isClearable
          isMulti={false}
          placeholder="Any"
          value={assigneeUser || null}
          onChange={handleChangeAssigneeUser}
          className='[&_[class*="control"]]:!p-0 [&_[class*="control"]]:min-w-[200px]'
        />
      </FilterItem>

      <FilterItem
        classes={{
          label: 'w-[85px] shrink-0',
          element: 'w-full min-w-[200px] [&>div>div]:gap-1 [&>div>div]:md:!gap-3',
        }}
        type="date"
        label={
          [TaskListMode.Upcoming, TaskListMode.AllTasks].includes(mode) ? 'Start date' : 'Due date'
        }
        showIcon={false}
        value={from_date && from_date ? [dayjs(from_date), dayjs(to_date)] : undefined}
        onChange={handleChangeDateFilter}
      />

      {mode === TaskListMode.History && (
        <FilterItem
          type="select"
          label="Result"
          placeholder="Any"
          classes={{
            label: 'w-[75px] shrink-0',
            element: 'w-full [&>div]:!min-w-[150px]',
          }}
          options={complianceOptions}
          value={filters.filter_by_compliance_status}
          onChange={handleChangeComplianceFilter}
        />
      )}
      {isFetching && <Spinner size="sm" variant="primary" className="m-0" />}
    </div>
  );
};

export default TasksFilters;
